import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { ChatMessage } from '../components/MessageItem';
import { getSocketBaseUrl } from '@/utils/apiConfig';
import { useNotificationSound } from './useNotificationSound';

interface UseSocketProps {
    orderId: string;
    orderNo: string;
    token: string;
    onError?: (error: string) => void;
    isWindowVisible?: boolean; // 新增：窗口是否可见
    onNewMessage?: () => void; // 新增：新消息通知回调函数
}

interface UseSocketReturn {
    socket: Socket | null;
    connected: boolean;
    messages: ChatMessage[];
    sendMessage: (content: string, messageType?: 'text' | 'image') => void;
    markAsRead: (maxMessageId?: string) => void; // 新增：标记已读函数
}

export const useSocket = ({ orderId, orderNo, token, onError, isWindowVisible = true, onNewMessage }: UseSocketProps): UseSocketReturn => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [connected, setConnected] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const socketRef = useRef<Socket | null>(null);

    // 使用 ref 来存储最新的窗口可见性状态，避免重连
    const isWindowVisibleRef = useRef(isWindowVisible);
    const onErrorRef = useRef(onError);
    const onNewMessageRef = useRef(onNewMessage);

    // 使用通知提示音hook
    // const { playNotificationSound } = useNotificationSound();

    // 更新 ref 的值
    useEffect(() => {
        isWindowVisibleRef.current = isWindowVisible;
        onErrorRef.current = onError;
        onNewMessageRef.current = onNewMessage;
    }, [isWindowVisible, onError, onNewMessage]);

    // 发送消息
    const sendMessage = (content: string, messageType: 'text' | 'image' = 'text') => {
        if (!socketRef.current || !connected) {
            return;
        }

        socketRef.current.emit('message', {
            orderNo: orderNo,
            senderRole: 'cp',
            messageType: messageType,
            content: content,
            isRead: false,
            createdAt: new Date().toISOString(),
        });
    };

    // 标记已读函数
    const markAsRead = (maxMessageId?: string) => {
        if (!socketRef.current || !connected) return;

        // 找到客户发送的消息中的最大ID
        let targetMessageId = maxMessageId;
        if (!targetMessageId) {
            const customerMessages = messages.filter(msg => msg.senderRole === 'customer' && msg.id);
            if (customerMessages.length > 0) {
                // 按时间排序，获取最新的消息ID
                const sortedMessages = customerMessages.sort((a, b) =>
                    new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
                );
                targetMessageId = sortedMessages[0].id;
            }
        }

        if (targetMessageId) {
            // console.log('客服标记消息已读，最大ID:', targetMessageId);
            socketRef.current.emit('markAsRead', {
                orderId: orderNo,
                maxMessageId: targetMessageId,
                readBy: 'cp'
            });
        }
    };

    // 检查是否有未读的客户消息
    const hasUnreadCustomerMessages = (messageList: ChatMessage[]) => {
        return messageList.some(msg =>
            msg.senderRole === 'customer' &&
            msg.isRead === false
        );
    };

    // 初始化Socket连接 - 移除 isWindowVisible 和 onError 从依赖数组
    useEffect(() => {
        if (!orderId || !token) return;

        const socketInstance = io(getSocketBaseUrl(), {
            query: {
                token: token,
                orderId: orderNo,
            },
            transports: ['websocket', 'polling'],
        });

        socketRef.current = socketInstance;

        // 连接事件
        socketInstance.on('connect', () => {
            console.log(`客服端聊天室连接成功 - 订单ID: ${orderId}`);
            setConnected(true);
        });

        socketInstance.on('disconnect', () => {
            console.log('客服端聊天室连接断开');
            setConnected(false);
        });

        socketInstance.on('connect_error', (error: any) => {
            console.error('客服端连接错误:', error);
            setConnected(false);
            onErrorRef.current?.('聊天室连接失败');
        });

        // 接收消息
        socketInstance.on('message', (data: any) => {
            const newMessage: ChatMessage = {
                id: data.id || Date.now().toString(),
                orderNo: data.orderNo || orderNo,
                senderRole: data.senderRole,
                messageType: data.messageType || 'text',
                content: data.content,
                isRead: data.isRead,
                readAt: data.readAt,
                createdAt: data.createdAt || new Date().toISOString(),
                updatedAt: data.updatedAt,
            };

            // 如果是客户发送的新消息，播放提示音（无论窗口是否可见）
            // if (newMessage.senderRole === 'customer') {
            //     // console.log('客服端收到新消息，播放提示音', onNewMessageRef.current);
            //     onNewMessageRef.current?.();
            // }

            setMessages(prev => {
                const newMessages = [...prev, newMessage];

                // 如果是客户发送的消息且窗口可见，自动标记已读
                if (newMessage.senderRole === 'customer' && isWindowVisibleRef.current && newMessage.id) {
                    setTimeout(() => {
                        console.log('客服端自动标记新消息已读:', newMessage.id);
                        socketInstance.emit('markAsRead', {
                            orderId: orderNo,
                            maxMessageId: newMessage.id,
                            readBy: 'cp'
                        });
                    }, 100); // 延迟1秒标记，模拟客服阅读时间
                }

                return newMessages;
            });
        });

        // 接收聊天历史
        socketInstance.on('chatHistory', (data: any[]) => {
            const historyMessages: ChatMessage[] = data.map(msg => ({
                id: msg.id || Date.now().toString(),
                orderNo: msg.orderNo || orderNo,
                senderRole: msg.senderRole,
                messageType: msg.messageType || 'text',
                content: msg.content,
                isRead: msg.isRead,
                readAt: msg.readAt,
                createdAt: msg.createdAt,
                updatedAt: msg.updatedAt,
            }));
            setMessages(historyMessages);

            // 加入房间时，如果有未读的客户消息，标记为已读
            if (hasUnreadCustomerMessages(historyMessages)) {
                setTimeout(() => {
                    const customerMessages = historyMessages.filter(msg => msg.senderRole === 'customer' && msg.id);
                    if (customerMessages.length > 0) {
                        const latestCustomerMessage = customerMessages.sort((a, b) =>
                            new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
                        )[0];

                        if (latestCustomerMessage.id) {
                            console.log('客服端加入房间时标记历史消息已读，最大ID:', latestCustomerMessage.id);
                            socketInstance.emit('markAsRead', {
                                orderId: orderNo,
                                maxMessageId: latestCustomerMessage.id,
                                readBy: 'cp'
                            });
                        }
                    }
                }, 500); // 短暂延迟确保连接稳定
            }
        });

        // 监听已读状态更新
        socketInstance.on('messageRead', (data: { messageId: string, readBy: string, readAt: string }) => {
            console.log('客服端收到消息已读确认:', data);
            setMessages(prev => prev.map(msg =>
                msg.id === data.messageId ? { ...msg, isRead: true, readAt: data.readAt } : msg
            ));
        });

        // 监听批量消息标记已读事件
        socketInstance.on('messagesMarkedAsRead', (data: {
            orderId: string,
            maxMessageId: string,
            readBy: string,
            updatedCount: number,
            timestamp: string
        }) => {
            console.log('客服端收到批量消息标记已读确认:', data);

            // 根据 readBy 和 maxMessageId 批量更新消息状态
            setMessages(prev => prev.map(msg => {
                // 如果是客户标记已读，更新客服发送的消息
                if (data.readBy === 'customer' && msg.senderRole === 'cp') {
                    // 更新指定消息ID及之前的消息为已读
                    if (msg.id && msg.id <= data.maxMessageId) {
                        return { ...msg, isRead: true, readAt: data.timestamp };
                    }
                }
                // 如果是客服标记已读，更新客户发送的消息
                else if (data.readBy === 'cp' && msg.senderRole === 'customer') {
                    // 更新指定消息ID及之前的消息为已读
                    if (msg.id && msg.id <= data.maxMessageId) {
                        return { ...msg, isRead: true, readAt: data.timestamp };
                    }
                }
                return msg;
            }));
        });

        // 用户加入提醒
        socketInstance.on('userJoined', (data: any) => {
            console.log('用户加入聊天室:', data);
        });

        setSocket(socketInstance);

        return () => {
            socketInstance.disconnect();
            socketRef.current = null;
        };
    }, [orderId, token, orderNo]); // 移除 onError 和 isWindowVisible

    return {
        socket,
        connected,
        messages,
        sendMessage,
        markAsRead,
    };
}; 